"use client";

import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import NotificationPoller from "@/components/NotificationPoller";
import EnvBadge, { EnvBadgeDot } from "@/components/EnvBadge";
import { useVisibilityPolling } from "@/hooks/useVisibilityPolling";

const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";
const POLL_INTERVAL = 30_000;

interface SidebarCounts {
  shipment_holds: number;
  returns_pending: number;
  approvals_pending: number;
  work_assignments_pending: number;
  as_active: number;
  purchase_active: number;
  leaves_pending: number;
  today_events: number;
  targets_at_risk: number;
  chat_unacked: number;
  team_chat_unread: number;
}

const TEAM_CHAT_LAST_READ_KEY = "team_chat_last_read_msg_id";
// 26차 후속 #23 — 사이드바 그룹 아코디언 펼침 상태 (2026-04-27)
const SIDEBAR_EXPANDED_GROUPS_KEY = "sidebar_expanded_groups";

// [ETC-001-PHASE3] 메뉴 정의는 backend canonical registry(GET /me/permissions)가 SoT 다.
// 이 파일에는 permission code / label / group / route 를 다시 정의하지 않는다.
// 아래 두 map 은 **순수 표현 metadata**(아이콘, 배지 연결)이며 permission code 로만 키를 잡는다.

type MenuItem = {
  code: string;
  key: string;      // 기존 경로형 catalog key (즐겨찾기 저장 계약)
  href: string;     // 이동 대상 route
  label: string;
  group: string;
};

type MenuGroup = {
  title: string;
  items: MenuItem[];
};

/** permission code → 접힘 사이드바 아이콘 (표현 전용) */
const ICON_BY_CODE: Record<string, string> = {
  "ops.dashboard": "📊",
  "ops.reports": "📈",
  "ops.team_chat": "💬",
  "ops.personal_notes": "📝",
  "ops.work_board": "📋",
  "inventory.product": "📦",
  "inventory.price_adjustment": "🏷️",
  "purchase.order": "🛒",
  "purchase.inbound_request": "📥",
  "inventory.cost_calculator": "🧮",
  "order.manual_intake": "✏️",
  "order.export": "📤",
  "order.invoice_split": "✂️",
  "order.daily_review": "🔍",
  "order.packing": "📬",
  "order.daiwa": "🎣",
  "order.customer_history": "👥",
  "order.shipment_hold": "🛑",
  "store.pos": "🏪",
  "store.notification": "🔔",
  "cs.return_inspection": "↩️",
  "cs.message_template": "💬",
  "as.request": "🔧",
  "as.checklist_settings": "✔️",
  "as.entitlement_policy": "🎟️",
  "as.entitlement_part_rule": "🧩",
  "hr.calendar": "📅",
  "hr.leave": "🌿",
  "settings.product_import": "⬆️",
  "settings.brand_sku": "🏷️",
  "settings.customer_db": "🗄️",
  "settings.approval": "✅",
  "settings.audit_log": "📝",
  "settings.erp_request": "🛠",
  "customer.identity": "🧬",
  "admin.account": "👤",
};

/** permission code → 사이드바 배지 (표현 전용). urgent 는 빨강 강조. */
const BADGE_BY_CODE: Record<string, { key: keyof SidebarCounts; urgent?: boolean }> = {
  "ops.dashboard": { key: "chat_unacked", urgent: true },
  "ops.reports": { key: "targets_at_risk", urgent: true },
  "ops.team_chat": { key: "team_chat_unread" },
  "ops.work_board": { key: "work_assignments_pending" },
  "purchase.order": { key: "purchase_active" },
  "order.daily_review": { key: "shipment_holds", urgent: true },
  "cs.return_inspection": { key: "returns_pending" },
  "as.request": { key: "as_active" },
  "hr.calendar": { key: "today_events" },
  "hr.leave": { key: "leaves_pending" },
  "settings.approval": { key: "approvals_pending", urgent: true },
};

/** GET /me/permissions 의 menus 항목(필요한 필드만). */
type PermissionMenu = {
  code: string;
  key: string;
  label: string;
  group: string;
  routes: string[];
  hidden: boolean;
  deprecated: boolean;
  source: string;
};

/**
 * 허용 menus → 사이드바 그룹 트리.
 * - hidden / deprecated 는 제외한다(확정 명세 2).
 * - 순서는 backend registry 순서를 그대로 따른다(정렬하지 않는다).
 */
function buildMenuGroups(menus: PermissionMenu[]): MenuGroup[] {
  const groups: MenuGroup[] = [];
  const byTitle = new Map<string, MenuGroup>();
  for (const m of menus) {
    if (m.hidden || m.deprecated) continue;
    const href = m.routes[0] ?? m.key;
    let g = byTitle.get(m.group);
    if (!g) {
      g = { title: m.group, items: [] };
      byTitle.set(m.group, g);
      groups.push(g);
    }
    g.items.push({ code: m.code, key: m.key, href, label: m.label, group: m.group });
  }
  return groups.filter((g) => g.items.length > 0);
}

function Badge({ count, urgent }: { count: number; urgent: boolean }) {
  if (!count || count <= 0) return null;
  return (
    <span
      className={`ml-auto text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center leading-tight ${
        urgent ? "bg-red-500 text-white" : "bg-orange-400 text-white"
      }`}
    >
      {count > 99 ? "99+" : String(count)}
    </span>
  );
}

export default function Sidebar() {
  const pathname = usePathname();
  const router = useRouter();

  const [mounted, setMounted] = useState(false);
  const [userName, setUserName] = useState("");
  const [userRole, setUserRole] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [counts, setCounts] = useState<SidebarCounts | null>(null);
  // [ETC-001-PHASE3] 메뉴는 GET /me/permissions 결과로만 만든다.
  //   null = 아직 로드 안 됨(로딩). 오류 시 permError 로 안내 + 재시도.
  //   **로드 실패 시 전체 메뉴를 여는 fallback 은 금지**(확정 명세 2).
  const [menus, setMenus] = useState<PermissionMenu[] | null>(null);
  const [permError, setPermError] = useState<string>("");
  const [favorites, setFavorites] = useState<string[]>([]);

  // 포장 출고 진입 시 사이드바 자동 축소
  const isPickingPage = pathname === "/picking" || pathname.startsWith("/picking/");
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (isPickingPage) {
      setCollapsed(true);
    }
  }, [isPickingPage]);

  // 26차 후속 #23 — 그룹별 펼침/접힘 상태. 즐겨찾기는 항상 펼침이라 여기 들어오지 않음.
  // 26차 후속 #24 — 사용자 수동 펼침/접힘이 route 이동 후에도 유지되도록 보정.
  //   · mount 1회 hydration: localStorage 저장값을 그대로 state 로 복원
  //   · pathname 변경: prev set 에 active group 만 add (덮어쓰지 않음). merge 결과를 localStorage 에도 반영
  //   · 사용자 토글: 즉시 localStorage 동기화
  // 빈 배열 자동 저장하지 않음 — toggleGroup / pathname 효과 두 곳 모두 prev 가 비어 있고
  // active group 도 없을 때는 setItem 을 호출하지 않아 기존 저장값을 절대 덮지 않음.
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const expandedHydratedRef = useRef(false);

  // 1) mount 1회 hydration
  useEffect(() => {
    if (expandedHydratedRef.current) return;
    expandedHydratedRef.current = true;
    try {
      const raw = localStorage.getItem(SIDEBAR_EXPANDED_GROUPS_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr) && arr.length > 0) {
          setExpandedGroups(new Set<string>(arr.filter((s) => typeof s === "string")));
        }
      }
    } catch {
      /* ignore */
    }
  }, []);

  // 2) pathname 변경 → prev 에 active group 만 add (다른 그룹 상태 유지)
  useEffect(() => {
    if (!menus) return;
    const activeGroup = buildMenuGroups(menus).find((g) =>
      g.items.some((i) => pathname === i.href || pathname.startsWith(i.href + "/")),
    );
    if (!activeGroup) return;
    setExpandedGroups((prev) => {
      if (prev.has(activeGroup.title)) return prev;
      const next = new Set(prev);
      next.add(activeGroup.title);
      try {
        localStorage.setItem(SIDEBAR_EXPANDED_GROUPS_KEY, JSON.stringify([...next]));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, [pathname, menus]);

  function toggleGroup(title: string) {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      try {
        localStorage.setItem(SIDEBAR_EXPANDED_GROUPS_KEY, JSON.stringify([...next]));
      } catch {
        /* ignore */
      }
      return next;
    });
  }

  const fetchCounts = useCallback(async () => {
    const token = localStorage.getItem("access_token");
    if (!token) return;
    try {
      const [countsRes, teamChatRes, permRes, favRes] = await Promise.all([
        fetch(`${API_URL}/sidebar/counts`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(
          `${API_URL}/chat/messages?entity_type=team&entity_id=general` +
            `&since_id=${parseInt(localStorage.getItem(TEAM_CHAT_LAST_READ_KEY) || "0", 10) || 0}` +
            `&limit=500`,
          { headers: { Authorization: `Bearer ${token}` } },
        ),
        fetch(`${API_URL}/me/permissions`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/me/menu-favorites`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (permRes.ok) {
        const permData = await permRes.json();
        setMenus((permData.menus || []) as PermissionMenu[]);
        setPermError("");
      } else {
        // 권한 조회 실패 — 전체 메뉴를 열지 않는다. 기존 메뉴가 있으면 유지, 없으면 오류 표시.
        setPermError(`메뉴 권한을 불러오지 못했습니다 (HTTP ${permRes.status})`);
      }
      if (favRes.ok) {
        const favData = await favRes.json();
        setFavorites(favData.favorites || []);
      }

      const base: SidebarCounts | null = countsRes.ok ? await countsRes.json() : null;
      let teamUnread = 0;
      if (teamChatRes.ok) {
        const data = await teamChatRes.json();
        const myName = localStorage.getItem("user_name") || "";
        // 본인 메시지는 제외
        teamUnread = (data.messages || []).filter(
          (m: { sender_name: string; is_system: boolean }) =>
            !m.is_system && m.sender_name !== myName,
        ).length;
      }

      if (base) {
        setCounts({ ...base, team_chat_unread: teamUnread });
      } else {
        setCounts((prev) =>
          prev ? { ...prev, team_chat_unread: teamUnread } : null,
        );
      }
    } catch {
      setPermError("메뉴 권한을 불러오지 못했습니다 (네트워크 오류)");
    }
  }, []);

  useEffect(() => {
    setUserName(localStorage.getItem("user_name") || "");
    setUserRole(localStorage.getItem("user_role") || "");
    setCompanyName(
      localStorage.getItem("user_company_name") ||
      localStorage.getItem("user_company_code") || ""
    );
    setMounted(true);
  }, []);

  // [ERP-POLLING-LOAD-REDUCTION-SAFE-DEV-20260707-035C]
  // 사이드바 카운트/미읽음 폴링 — 30초 유지. hidden 탭 중지 + visible 복귀 즉시 1회 fetch,
  // 토큰없음 중지(useVisibilityPolling). 기존 counts+chat/messages+perm+fav 묶음 호출 그대로.
  useVisibilityPolling(fetchCounts, { activeMs: POLL_INTERVAL });

  function handleLogout() {
    if (!confirm("로그아웃하시겠습니까?")) return;
    // [ERP-POLLING-LOAD-REDUCTION-SAFE-DEV-20260707-035C]
    // 폴링 정지는 useVisibilityPolling 가 담당(토큰 제거 시 다음 tick 중지 + 언마운트 cleanup).
    localStorage.removeItem("access_token");
    localStorage.removeItem("user_name");
    localStorage.removeItem("user_role");
    localStorage.removeItem("user_company_code");
    localStorage.removeItem("user_company_name");
    router.push("/login");
  }

  const roleLabels: Record<string, string> = {
    admin:     "관리자",
    inventory: "재고",
    as:        "AS",
    store:     "영업",
    repair:    "수리",
    viewer:    "조회",
  };

  async function toggleFavorite(menu_key: string) {
    const token = localStorage.getItem("access_token");
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/me/menu-favorites`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ menu_key }),
      });
      if (res.ok) {
        const d = await res.json();
        setFavorites(d.favorites || []);
      } else if (res.status === 400) {
        const err = await res.json().catch(() => ({}));
        alert(err?.detail || "즐겨찾기 변경 실패");
      }
    } catch {
      /* ignore */
    }
  }

  // [ETC-001-PHASE3] 허용된 menus 에서만 만든다. 로딩 중(null)이면 빈 목록 —
  // 권한을 모르는 상태에서 메뉴를 열어주지 않는다(fallback 금지).
  const visibleGroups: MenuGroup[] = menus ? buildMenuGroups(menus) : [];
  const allItems: MenuItem[] = visibleGroups.flatMap((g) => g.items);
  const favoriteItems: MenuItem[] = favorites
    .map((k) => allItems.find((i) => i.key === k || i.href === k))
    .filter((i): i is MenuItem => !!i);
  const permLoading = menus === null && !permError;

  if (collapsed) {
    return (
      <aside className="w-16 min-h-screen bg-gray-900 text-white flex flex-col items-center py-3 shrink-0">
        {/* 펼치기 버튼 */}
        <button
          onClick={() => setCollapsed(false)}
          className="w-10 h-10 flex items-center justify-center rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors mb-2"
          title="메뉴 펼치기"
        >
          ☰
        </button>
        {/* [ERP-ENV-VISIBILITY-BANNER-DEV-20260626-001] 접힘 상태 — 환경 색점만 compact 표시. */}
        <EnvBadgeDot className="mb-2" />
        {/* 주요 메뉴 아이콘 */}
        <nav className="flex-1 flex flex-col gap-1 w-full px-1 overflow-y-auto">
          {permError && (
            <button
              type="button"
              onClick={() => { setPermError(""); fetchCounts(); }}
              title={`${permError} — 다시 시도`}
              className="w-full h-10 flex items-center justify-center rounded-lg text-red-400 hover:bg-gray-700"
            >
              ⚠
            </button>
          )}
          {[
            ...favoriteItems,
            ...allItems.filter((i) => !favorites.includes(i.key)),
          ].map((item) => {
            const isActive = pathname === item.href || pathname.startsWith(item.href + "/");
            const badge = BADGE_BY_CODE[item.code];
            const count = badge && counts ? counts[badge.key] : 0;
            return (
              <Link
                key={item.code}
                href={item.href}
                title={item.label}
                className={`relative flex items-center justify-center w-full h-10 rounded-lg text-lg transition-colors ${
                  isActive ? "bg-blue-600" : "text-gray-400 hover:bg-gray-700 hover:text-white"
                }`}
              >
                {ICON_BY_CODE[item.code] || "•"}
                {count > 0 && (
                  <span className="absolute top-0.5 right-0.5 w-4 h-4 text-[9px] font-bold bg-red-500 text-white rounded-full flex items-center justify-center leading-none">
                    {count > 9 ? "9+" : count}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
        {mounted && <NotificationPoller />}
      </aside>
    );
  }

  return (
    <aside className="w-60 min-h-screen bg-gray-900 text-white flex flex-col shrink-0">
      {/* 로고 + 축소 버튼 */}
      <div className="px-5 pt-5 pb-4 border-b border-gray-700">
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <h1 className="text-base font-bold text-white tracking-tight leading-tight">HITCAMP ERP</h1>
            <p className="text-xs text-gray-400 mt-0.5">히트캠프 운영 시스템</p>
            {mounted && userName && (
              <p className="text-xs text-blue-300 mt-1 truncate" title={userName}>
                👋 {userName}{userRole === "admin" ? " (관리자)" : ""}
              </p>
            )}
          </div>
          <button
            onClick={() => setCollapsed(true)}
            className="ml-2 shrink-0 text-gray-500 hover:text-white transition-colors text-lg"
            title="사이드바 축소"
          >
            ◀
          </button>
        </div>
        {mounted && companyName && (
          <span className="mt-2 inline-block text-xs font-semibold bg-blue-900 text-blue-300 border border-blue-700 px-2 py-0.5 rounded">
            {companyName}
          </span>
        )}
        {/* [ERP-ENV-VISIBILITY-BANNER-DEV-20260626-001] 환경 가시성 배지 — 사용자/회사 정보 아래. */}
        <EnvBadge variant="dark" className="mt-2" />
        {/* [ERP-SIDEBAR-MOBILE-SHORTCUT-CHAT-ENTER-BEHAVIOR-DEV-20260702-023]
            모바일 기기 바로가기가 PC 화면으로 진입할 때 사용자가 직접 모바일 전용 화면으로
            이동할 수 있는 바로가기. 자동 리다이렉트 로직은 건드리지 않음. */}
        <Link
          href="/mobile"
          className="mt-2 flex items-center justify-center gap-1.5 w-full px-2 py-1.5 rounded-md bg-gray-800 hover:bg-gray-700 text-gray-200 hover:text-white text-xs font-medium border border-gray-700 transition-colors"
          title="모바일 전용 화면으로 이동"
        >
          📱 모바일
        </Link>
      </div>

      {/* 메뉴 */}
      <nav className="flex-1 px-2 py-3 overflow-y-auto space-y-4">
        {/* [ETC-001-PHASE3] 메뉴 권한 로딩/오류 상태.
            권한을 모르는 동안 메뉴를 열어주지 않는다(전체 메뉴 fallback 금지).
            로그인/로그아웃 동작은 그대로 유지된다. */}
        {permLoading && (
          <p className="px-3 py-2 text-xs text-gray-500">메뉴 권한 불러오는 중...</p>
        )}
        {permError && (
          <div className="mx-1 rounded-lg border border-red-700 bg-red-950/40 px-3 py-2">
            <p className="text-xs text-red-300 leading-snug">{permError}</p>
            <button
              type="button"
              onClick={() => { setPermError(""); fetchCounts(); }}
              className="mt-1.5 text-[11px] font-semibold text-red-200 underline hover:text-white"
            >
              다시 시도
            </button>
          </div>
        )}
        {!permLoading && !permError && visibleGroups.length === 0 && (
          <p className="px-3 py-2 text-xs text-gray-500 leading-snug">
            접근 가능한 메뉴가 없습니다.
            <br />
            관리자에게 권한을 요청하세요.
          </p>
        )}
        {favoriteItems.length > 0 && (
          <div>
            {/* 26차 후속 #23 — 즐겨찾기 헤더의 ⭐ 프리픽스 제거. 메뉴 행의 별 토글과 시각적 중복 방지. */}
            <p className="px-3 mb-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
              즐겨찾기
            </p>
            <div className="space-y-0.5">
              {favoriteItems.map((item) => {
                const isActive = pathname === item.href || pathname.startsWith(item.href + "/");
                const badge = BADGE_BY_CODE[item.code];
                const count = badge && counts ? counts[badge.key] : 0;
                return (
                  <Link
                    key={`fav-${item.code}`}
                    href={item.href}
                    className={`group flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-blue-600 text-white"
                        : "text-gray-300 hover:bg-gray-800 hover:text-white"
                    }`}
                  >
                    <span className="flex-1 min-w-0 truncate">{item.label}</span>
                    {badge && <Badge count={count} urgent={badge.urgent ?? false} />}
                    {/* 26차 후속 #23 — 즐겨찾기 행의 ⭐도 기본 흐림(40%), hover 시 진하게. */}
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavorite(item.key); }}
                      className="ml-1 text-xs text-yellow-400 opacity-40 group-hover:opacity-90 hover:!opacity-100 transition-opacity"
                      title="즐겨찾기 제거"
                      aria-label="즐겨찾기 제거"
                    >
                      ⭐
                    </button>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
        {/* 26차 후속 #23 — 즐겨찾기 외 그룹은 아코디언. 헤더 클릭으로 펼침/접힘. 활성 메뉴가 속한 그룹은 자동 펼침. */}
        {visibleGroups.map((group) => {
          const isExpanded = expandedGroups.has(group.title);
          const hasActiveItem = group.items.some(
            (i) => pathname === i.href || pathname.startsWith(i.href + "/"),
          );
          // 접혀 있어도 그룹 내 미확인 카운트가 있으면 헤더에 작은 합계 점 표시
          const groupBadgeTotal =
            !isExpanded && counts
              ? group.items.reduce((sum, i) => {
                  const b = BADGE_BY_CODE[i.code];
                  return sum + (b && counts ? counts[b.key] : 0);
                }, 0)
              : 0;
          return (
            <div key={group.title}>
              <button
                type="button"
                onClick={() => toggleGroup(group.title)}
                className={`w-full px-3 mb-1 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider transition-colors ${
                  hasActiveItem ? "text-gray-300" : "text-gray-500 hover:text-gray-300"
                }`}
                aria-expanded={isExpanded}
              >
                <span
                  className={`inline-block transition-transform text-[9px] ${
                    isExpanded ? "rotate-90" : ""
                  }`}
                >
                  ▶
                </span>
                <span className="flex-1 text-left">{group.title}</span>
                {groupBadgeTotal > 0 && (
                  <span className="ml-auto w-1.5 h-1.5 rounded-full bg-orange-400" aria-hidden />
                )}
              </button>
              {isExpanded && (
                <div className="space-y-0.5">
                  {group.items.map((item) => {
                    const isActive = pathname === item.href || pathname.startsWith(item.href + "/");
                    const badge = BADGE_BY_CODE[item.code];
                    const count = badge && counts ? counts[badge.key] : 0;
                    const isFav = favorites.includes(item.key);
                    return (
                      <Link
                        key={item.code}
                        href={item.href}
                        className={`group flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          isActive
                            ? "bg-blue-600 text-white"
                            : "text-gray-300 hover:bg-gray-800 hover:text-white"
                        }`}
                      >
                        <span className="flex-1 min-w-0 truncate">{item.label}</span>
                        {badge && <Badge count={count} urgent={badge.urgent ?? false} />}
                        {/* 26차 후속 #23 — 별표 시각 소음 감소.
                            · 즐겨찾기 등록된 행: 기본 40% 흐림, hover 진해짐
                            · 미등록 행: 기본 숨김(0%), 그룹 hover 시 30% 살짝 보임 */}
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavorite(item.key); }}
                          className={`ml-1 text-xs transition-opacity ${
                            isFav
                              ? "text-yellow-400 opacity-40 group-hover:opacity-90 hover:!opacity-100"
                              : "text-gray-400 opacity-0 group-hover:opacity-50 hover:!opacity-100 hover:!text-yellow-300"
                          }`}
                          title={isFav ? "즐겨찾기 제거" : "즐겨찾기 추가"}
                          aria-label={isFav ? "즐겨찾기 제거" : "즐겨찾기 추가"}
                        >
                          {isFav ? "⭐" : "☆"}
                        </button>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* 사용자 정보 + 로그아웃 */}
      <div className="px-4 py-4 border-t border-gray-700">
        <div className="flex items-center justify-between mb-2">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white truncate">
              {mounted ? userName : ""}
            </p>
            <p className="text-xs text-gray-400">
              {mounted ? (roleLabels[userRole] ?? userRole) : ""}
            </p>
          </div>
          <button
            onClick={handleLogout}
            className="ml-2 shrink-0 text-xs text-gray-400 hover:text-white border border-gray-600 hover:border-gray-400 px-2 py-1 rounded transition-colors"
          >
            로그아웃
          </button>
        </div>
      </div>
      {/* 전역 알림 팝업 폴러 */}
      {mounted && <NotificationPoller />}
    </aside>
  );
}
